'use strict';

import React, {Component, useState} from 'react';
import PropTypes from "prop-types";
import {observer} from "mobx-react";
import classnames from "classnames";

import {FaExclamationCircle} from "react-icons/fa";

import Form from 'react-bootstrap/Form';
import Alert from "react-bootstrap/Alert";

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import SimpleState from "@uw-it-sis/lib-react/lib/SimpleState";
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import {withRouter} from "@uw-it-sis/lib-react/lib/WithRouter";
import {capitalize, guid} from "@uw-it-sis/lib-react/lib/Utils";

import {Campus} from '@uw-it-sis/lib-react/lib/AppConstants';
import optionalChargesStore from "./OptionalChargesStore";
import OpChargeUpass from "./OpChargeUpass";
import OpChargeInsurance from "./OpChargeInsurance";
import OpChargeWashPIRG from "./OpChargeWashPIRG";
import OpChargeUSAS from "./OpChargeUSAS";
import OpChargeWSA from "./OpChargeWSA";
import OpChargeASUW from "./OpChargeASUW";


const NotEligible = (props) => {
    if (props.campus === Campus.seattle) {
        let suffix;
        if (parseInt(props.data.resident_code) > 0) {
            suffix = "and therefore cannot sign up for international insurance or contribute to any student organization.";
        } else {
            suffix = "and therefore cannot contribute to any student organization.";
        }

        return (
            <p>You are currently not <a href='https://registrar.washington.edu/course-registration/' target='_regelig'>eligible to register for classes</a> for {props.termName} {suffix}</p>
        )
    } else {
        return (
            <p>You are currently not eligible to register for classes for {props.termName} and therefore cannot sign up for insurance, purchase a U-PASS or make an ASUW selection.</p>
        )
    }
}

NotEligible.propTypes = {
    data: PropTypes.object.isRequired,
    campus: PropTypes.string.isRequired,
    termName: PropTypes.string.isRequired
}

class OptionalCharges extends Component {
    constructor(props) {
        super(props);
        this.props = props;
        this.store = optionalChargesStore;
        this.seamlessRegEnabled = appStore.getAppAttribute("seamless_reg_enabled");

        this.analyticsStore = appStore.getAnalyticsStore();

        if (!this.props.termId && this.props.hasOwnProperty("params") && this.props.params.hasOwnProperty("identifier")) {
            const identifier = this.props.params.identifier;
            this.termId = TermUtils.makeTermIdFromTermName(identifier);
        } else {
            this.termId = this.props.termId;
        }
    }

    componentDidMount() {
        if (this.seamlessRegEnabled) {
            this.store.mount(this.props.onSuccess, this.termId);
        }
    }

    buildDoneComponent = () => {
        // Data returned via API
        let data = this.store.data;
        let component;
        // variables used that differ for each campus
        let campus, asuwAbbr, asuwLink, upassInfoLink, upassTermsLink, insHomeLink;
        switch (parseInt(data.branch_code)) {
            case 0:
                campus = Campus.seattle;
                asuwAbbr = "ASUW";
                asuwLink = <a href='http://hub.washington.edu/servicepage.php?q=asuw' target='_blank'>Associated Students of the University of Washington</a>;
                upassInfoLink = "http://www.washington.edu/u-pass/";
                upassTermsLink = upassInfoLink;
                insHomeLink = "http://www.washington.edu/ship/";
                break;
            case 1:
                campus = Campus.bothell;
                asuwAbbr = "ASUWB";
                asuwLink = <><a href='https://www.uwb.edu/asuwb' target='_blank'>Associated Students of the UW Bothell</a> ({asuwAbbr})</>;
                upassInfoLink = "http://www.uwb.edu/administration-planning/facilities/transportation/upass";
                upassTermsLink = "http://www.washington.edu/u-pass/";
                insHomeLink = "http://www.uwb.edu/students/services/insurance.xhtml";
                break;
            case 2:
                campus = Campus.tacoma;
                asuwAbbr = "ASUWT";
                asuwLink = <><a href='http://www.tacoma.washington.edu/asuwt/' target='_blank'>Associated Students of the University of Washington Tacoma</a> ({asuwAbbr})</>;
                upassInfoLink = "http://www.tacoma.uw.edu/getting-campus/what-u-pass";
                upassTermsLink = "http://www.tacoma.uw.edu/getting-campus/u-pass-program-terms-use";
                insHomeLink = "http://www.tacoma.washington.edu/studentaffairs/SHW/shw_insurance.cfm";
                break;
        }
        // variables used throughout page
        const termName = TermUtils.makeTermDescriptionFromTermId(this.termId);



        if (data.eligible_sw === "N") {
            component = <NotEligible data={data} campus={campus} termName={termName} />
        } else {
            component =
                <>
                    <OpChargeUpass campus={campus} />
                    <hr aria-hidden={true} className="my-4"/>
                    <OpChargeInsurance campus={campus} termName={termName} termId={this.termId} />
                    <hr aria-hidden={true} className="my-4"/>
                    <OpChargeWashPIRG campus={campus} />
                    <hr aria-hidden={true} className="my-4"/>
                    <OpChargeUSAS campus={campus} />
                    <hr aria-hidden={true} className="my-4"/>
                    <OpChargeWSA campus={campus} />
                    <hr aria-hidden={true} className="my-4"/>
                    <OpChargeASUW campus={campus} />
                </>
        }

        let error;
        if (data.hasOwnProperty("error_msg") && data.error_msg !== "") {
            error =
                <Alert variant="danger">
                    {data.error_msg}
                </Alert>
        }



        return (
            <>
                <h1>Insurance/Optional Charges - {termName}</h1>
                {error}
                {component}
            </>
        )
    }

    render() {
        if (!this.seamlessRegEnabled) {
            return (
                <Alert variant="danger" className="d-inline-flex align-items-center">
                    <FaExclamationCircle className="me-3 fs-5" aria-hidden={true} />
                    <div>This page is not currently accessible.</div>
                </Alert>
            )
        } else {
            return (
                <SimpleState state={this.store.state}
                             doneComponentBuilder={() => this.buildDoneComponent()}
                             pendingComponentBuilder="dots"
                             name="OptionalCharges" />
            )
        }
    }
}

OptionalCharges.propTypes = {
    onSuccess: PropTypes.func,
    termId: PropTypes.string
}

OptionalCharges.defaultProps = {
    onSuccess: () => {},
    termId: ""
}

export default withRouter(observer(OptionalCharges));
export {NotEligible}